import React from 'react';

import { debounce } from 'lodash';

import * as convert from '_core/utils/convert';
import * as storage from '_core/utils/storage';
import * as cache from '_core/utils/cache';
import * as math from '_core/utils/math';
import * as api from '_core/utils/api';
import { useVisitorInfo } from '_core/utils/visitorInfo';
import { getWarehouseCurrencyId } from '_core/utils/shopCurrency';
import usePromise from '_core/hooks/usePromise';
import useCartDeliveryInfo from '_core/hooks/useCartDeliveryInfo';
import { withLang } from '_core/hocs/withLang';

import { CURRENCY_FORMAT_CHAR, SHOP_GOODS_SHOP_ID, SHOP_GOODS_FIELDS } from 'basic/config';


const GOODS_REST_TIMEOUT = 5 * 60000; // 5 minutes

const STORAGE_CART_DATA_KEY = 'shop-cart-data';

const STORAGE_FAVORITE_DATA_KEY = 'shop-favorite-data';

const DEFAULT_CURRENCY = {
  code: '',
  title: '',
  scale: 0,
  format: CURRENCY_FORMAT_CHAR
};

const QUERY_INFO_FIELDS = {
  length: { byGoodsList: (goods) => goods.length },
  
  count: { byGoodsGroup: (group) => group.count },
  price: { byGoodsGroup: (group) => group.price * group.count },
  discount: { byGoodsGroup: (group) => group.discount * group.count },
  points: { byGoodsGroup: (group) => group.points * group.count },
  
  favorite_length: { byFavoriteList: (favorites) => favorites.length }
};


export const CartContext = React.createContext();

export const CartProvider = withLang(({ langInfo, children, replaceCurrencyFromWarehouse }) => {
  const visitorInfo = useVisitorInfo();
  
  const [categoryList, setCategoryList] = React.useState([]);
  const [groupOrderCurrent, setGroupOrderCurrent] = React.useState();
  const [goodsState, setGoodsState] = React.useState(null);
  const [goodsInfo, setGoodsInfo] = React.useState({});
  const [goodsRest, setGoodsRest] = React.useState({});
  const [currency, setCurrency] = React.useState(DEFAULT_CURRENCY);
  const [deliveryOptions, setDeliveryOptions] = React.useState();
  
  const visitorId = visitorInfo.info?.id;
  
  React.useEffect(() => {
    api.getShopGoodsCategoryEx(visitorInfo)
      .then(setCategoryList);
  }, [visitorId]);
  
  const modalRef = React.useRef();
  const modalBtnRef = React.useRef();
  const loadDataIterationRef = React.useRef(0);
  
  const currencyList = usePromise((visitorId) => api.getMoneyCurrencyList(visitorId).then((data) => { if (data.list?.length) setCurrency((currency) => data.current.id === currency.id ? currency : data.list.find((item) => item.id === data.current.id) || data.list[0]); return data; }), []);
  
  const storeData = React.useCallback(debounce((currency, state) => {
    storageStoreData(currency, state, 'goods');
  }, 500), []);
  
  const storeFavoriteData = React.useCallback(debounce((currency, state) => {
    storageStoreData(currency, state, 'favorites');
  }, 500), []);
  
  const loadApiData = (currency, data, update) => {
    setGoodsState((state) => {
      if (state.update !== update || state.syncCurrencyCode !== currency?.code)
        return state;
      
      state = {
        ...state,
        
        isLoading: false,
        goods: buildApiGoods(data.list),
        message: data.message,
        isNext: data.next,
        isPromocode: data.promocode
      };
      
      calcQueryInfo(state);
      storeData(currency, state);
      storeFavoriteData(currency, state);
      
      return state;
    });
  };
  
  const fetchData = React.useCallback(debounce((currency, warehouse, state) => {
    const update = state?.update || 0;
    
    api.request('store/cart/sync', { currency: currency.code, warehouse })
      .then((data) => loadApiData(currency, data, update))
      .catch((e) => null);
  }, 500), []);
  
  const submitData = React.useCallback(debounce((currency, warehouse, state) => {
    const update = state?.update || 0;
    
    const list = serializeApiList(state);
    
    api.request('store/cart/sync', { currency: currency.code, warehouse, list })
      .then((data) => loadApiData(currency, data, update))
      .catch((e) => null);
  }, 500), []);
  
  const setGoodsInfoMap = (map) => map && setGoodsInfo((goodsInfo) => {
      let modified = null;
      
      Object.entries(map).forEach(([id, item]) => {
        if (math.deepEqual(goodsInfo[id], item))
          return;
        
        if (!modified)
          modified = { ...goodsInfo };
        
        modified[id] = item;
      });
      
      return modified || goodsInfo;
    });
  
  const setGoodsRestMap = (map) => map && setGoodsRest((rest) => {
      let modified = null;
      
      Object.entries(map).forEach(([key, val]) => {
        val = convert.toInt(val);
        
        if (rest[key] === val)
          return;
        
        if (!modified)
          modified = { ...rest };
        
        modified[key] = val;
      });
      
      return modified || rest;
    });
  
  const cart = {
    ...goodsState,
    
    get isLoading() {
      return goodsState?.isLoading ?? true;
    },
    
    get query() {
      return goodsState?.query || {};
    },
    
    getQuery(target) {
      if (target === 'all')
        return cart.query;
      
      target = target?.id || target || cart.groupOrderCurrentTarget.id;
      
      return goodsState?.queryTarget?.[target] || {};
    },
    
    getGoodsList(target) {
      target = target?.id || target || cart.groupOrderCurrentTarget.id;
      
      return goodsState?.goods?.filter((item) => item.target === target);
    },
    
    getControl: (goods, target) => {
      const id = convert.toUInt(goods?.goods?.id || goods?.id);
      
      target = target?.id || target || cart.groupOrderCurrentTarget.id;
      
      if (!cart.deliveryInfo.isRequest && !cart.deliveryInfo.isConfirmed) {
        cart.deliveryInfo.isRequest = true;
        
        cart.deliveryInfo.setIsRequest(true);
      }
      
      const control = {
        id,
        target,
        
        get info() {
          if (!goods?.id)
            return { ...goods, group: [], isLoading: false };
          
          const data = control.data;
          
          if (!goodsState || typeof Window === 'undefined')
            return { ...goods, ...data, isLoading: true };
          
          api.getShopGoods(goods.id, { shop: SHOP_GOODS_SHOP_ID, visitor_group: visitorInfo.info?.visitor_group.join(','), fields: SHOP_GOODS_FIELDS.list })
            .then(setGoodsInfoMap)
            .catch((error) => {
              console.error(error);
              
              setGoodsInfoMap([]);
            });
          
          const info = goodsInfo[goods.id];
          
          if (typeof info === 'undefined')
            return { ...goods, ...data, isLoading: true };
          
          if (!info && data?.group) {
            setTimeout(() => control.remove(), 1);
            
            return { ...goods, ...data, isLoading: true };
          }
          
          const category = categoryList.find((category) => category.id === info.category);
          
          return { ...info, ...data, category, isLoading: false };
        },
        
        get data() {
          return id && goodsState
            ?.goods
            ?.find((item) => item.id === id && item.target === target) || null;
        },
        
        get count() {
          return control.data
            ?.group
            ?.reduce((prev, item) => item.is_modifiable ? prev + item.count : prev, 0) || 0;
        },
        
        get countTotal() {
          return control.data
            ?.group
            ?.reduce((prev, item) => prev + item.count, 0) || 0;
        },
        
        setCount: (callback) => {
          if (!id || !callback)
            return;
          
          setGoodsState((state) => {
            if (!state)
              return state;
            
            let goodsStored = true;
            
            let goodsItem = state.goods?.find((item) => item.id === id && item.target === target);
            
            if (!goodsItem) {
              goodsStored = false;
              
              goodsItem = { id, target, group: [] };
            }
            
            const rest = control.rest;
            
            const oldCount = goodsItem.group.reduce((prev, item) => item.is_modifiable ? prev + item.count : prev, 0) || 0;
            
            const newCount = Math.min(convert.toUInt(callback(oldCount)), rest.isLoading ? Infinity : rest.value);
            
            //console.log('----------[', newCount, '] - [', oldCount, '] = [', newCount - oldCount, '], rest: [', rest.isLoading ? Infinity : rest.value, ']');
            
            if (newCount === oldCount)
              return state;
            
            state = { ...state, update: state.update + 1 };
            
            if (newCount > oldCount) {
              if (!goodsStored)
                state.goods = state.goods ? [...state.goods, goodsItem] : [goodsItem];
              
              let groupItem = goodsItem.group.find((item) => item.is_modifiable);
              
              if (!groupItem) {
                goodsItem.group.unshift(groupItem = {
                  count: 0,
                  
                  price: convert.toUFloat(goods.price),
                  discount: convert.toUFloat(goods.discount),
                  points: convert.toUFloat(goods.points),
                  
                  is_modifiable: true
                });
              }
              
              groupItem.count += newCount - oldCount;
            } else {
              let diff = oldCount - newCount;
              
              goodsItem.group = goodsItem.group.filter((item) => {
                if (diff <= 0 || !item.is_modifiable)
                  return item.count > 0;
                
                const countSub = Math.max(Math.min(item.count, diff), 0);
                
                item.count -= countSub;
                diff -= countSub;
                
                return item.count > 0;
              });
              
              if (!goodsItem.group.length)
                state.goods = state.goods?.filter((item) => item.group.length > 0);
            }
            
            calcQueryInfo(state);
            
            storeData(cart.shopCurrency, state);
            submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
            
            return state;
          });
        },
        
        remove: () => {
          control.setCount((count) => 0);
        },
        
        decrease: () => {
          control.setCount((count) => count - 1);
        },
        
        increase: () => {
          control.setCount((count) => count + 1);
        },
        
        toggle: () => {
          control.setCount((count) => count ? 0 : 1);
        },
        
        // favorites
        
        get favoriteData() {
          return id && goodsState
            ?.favorites
            ?.find((item) => item.id === id) || null;
        },
        
        get isFavorite() {
          return !!control.favoriteData;
        },
        
        toggleFavorite(value) {
          if (!id)
            return;
          
          setGoodsState((state) => {
            if (!state)
              return state;
            
            if (!value && value !== false)
              value = !state.favorites?.some((item) => item.id === id);
            
            if (value) {
              if (!state.favorites?.some((item) => item.id === id)) {
                state = { ...state, update: state.update + 1 };
                
                if (!state.favorites)
                  state.favorites = [];
                
                let group = null;
                
                if (!goods.price && Array.isArray(goods.group) && goods.group.length) {
                  group = goods.group.find((item) => item.is_modifiable);
                  
                  if (!group)
                    group = goods.group[0];
                  
                  if (group) {
                    group = {
                      count: 1,
                      
                      price: convert.toUFloat(group.price),
                      discount: convert.toUFloat(group.discount),
                      points: convert.toUFloat(group.points),
                      
                      is_modifiable: true
                    };
                  }
                }
                
                if (!group) {
                  group = {
                    count: 1,
                    
                    price: convert.toUFloat(goods.price),
                    discount: convert.toUFloat(goods.discount),
                    points: convert.toUFloat(goods.points),
                    
                    is_modifiable: true
                  };
                }
                
                state.favorites.push({ id, group: [group] });
              }
            } else {
              if (state.favorites) {
                state = {
                  ...state,
                  
                  update: state.update + 1,
                  favorites: state.favorites.filter((item) => item.id !== id)
                };
              }
            }
            
            calcQueryInfo(state);
            
            storeFavoriteData(cart.shopCurrency, state);
            
            return state;
          });
        },
        
        // rest
        
        get rest() {
          if (!id || !cart.deliveryInfo.warehouse)
            return { isLoading: false, value: 0 };
          
          if (cart.deliveryInfo.warehouse.is_infinity_goods)
            return { isLoading: false, value: Infinity };
          
          if (!goodsState || typeof Window === 'undefined')
            return { isLoading: true, value: 0 };
          
          cache.get(
              null,
              ['shop-goods-rest', cart.deliveryInfo.warehouse.id],
              (context, reduce) => api.request('delivery/warehouse/rest', { data: { [cart.deliveryInfo.warehouse.id]: reduce } }).then((data) => data[cart.deliveryInfo.warehouse.id]),
              GOODS_REST_TIMEOUT, // clearMillis
              id, // reduceKey
              100 // reduceMillis
            )
            .then(setGoodsRestMap)
            .catch((error) => {
              console.error(error);
              
              setGoodsRestMap(error.result);
            });
          
          const rest = goodsRest[id];
          
          if (typeof rest === 'undefined')
            return { isLoading: true, value: 0 };
          
          return { isLoading: false, value: rest || 0 };
        }
      };
      
      return control;
    },
    
    clearMessage: () => setGoodsState((state) => ({ ...state, message: null })),
    
    isDeliveryRequired: () => {
      const result = { isLoading: false, value: true };
      
      cart.goods.forEach((goods) => {
        const control = cart.getControl(goods);
        
        const info = control.info;
        
        if (info.isLoading) {
          result.isLoading = true;
          result.value = false;
        } else if (!info.is_delivery_required) {
          result.value = false;
        }
      });
      
      return result;
    },
    
    agreementArticleList: () => {
      const result = { isLoading: false, value: [] };
      
      cart.goods.forEach((goods) => {
        const control = cart.getControl(goods);
        
        const info = control.info;
        
        if (info.isLoading) {
          result.isLoading = true;
        } else if (info.agreement_article_list?.length) {
          result.value.push(...info.agreement_article_list);
        }
      });
      
      return result;
    },
    
    getGroupControl: (goods, index) => {
      const id = convert.toUInt(goods?.id);
      
      index = convert.toUInt(index);
      
      const control = cart.getControl(goods);
      
      const target = control.target;
      
      const groupControl = {
        get data() {
          return control.data;
        },
        
        get count() {
          return groupControl.data
            ?.group?.[index]
            ?.count || 0;
        },
        
        setCount: (callback) => {
          if (!id || !callback)
            return;
          
          setGoodsState((state) => {
            if (!state)
              return state;
            
            let goodsItem = state.goods?.find((item) => item.id === id && item.target === target);
            
            if (!goodsItem)
              return state;
            
            let groupItem = goodsItem.group[index];
            
            if (!groupItem)
              return state;
            
            const rest = groupControl.rest;
            
            const oldCount = groupItem.count;
            
            const newCount = Math.min(convert.toUInt(callback(oldCount)), rest.isLoading ? Infinity : rest.value);
            
            if (newCount === oldCount)
              return state;
            
            state = { ...state, update: state.update + 1 };
            
            groupItem.count = newCount;
            
            calcQueryInfo(state);
            
            storeData(cart.shopCurrency, state);
            submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
            
            return state;
          });
        },
        
        remove: () => {
          groupControl.setCount((count) => 0);
        },
        
        decrease: () => {
          groupControl.setCount((count) => count - 1);
        },
        
        increase: () => {
          groupControl.setCount((count) => count + 1);
        },
        
        toggle: () => {
          groupControl.setCount((count) => count ? 0 : 1);
        },
        
        // favorites
        
        get favoriteData() {
          return control.favoriteData;
        },
        
        get isFavorite() {
          return control.isFavorite;
        },
        
        toggleFavorite(value) {
          return control.toggleFavorite(value);
        },
        
        // rest
        
        get rest() {
          const rest = control.rest;
          
          if (rest.isLoading)
            return rest;
          
          const goodsItem = goodsState.goods?.find((item) => item.id === id && item.target === target);
          
          if (!goodsItem)
            return rest;
          
          const groupItem = goodsItem.group[index];
          
          if (!groupItem)
            return rest;
          
          const restValue = rest.value - goodsItem.group.reduce((prev, item) => item === groupItem ? prev : prev + item.count, 0);
          
          if (rest.value === restValue)
            return rest;
          
          return { isLoading: false, value: restValue };
        }
      };
      
      return groupControl;
    },
    
    fetchData: () => fetchData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, goodsState),
    
    submitData: () => submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, goodsState),
    
    clear: () => {
      setTimeout(() => setGoodsState((state) => {
        if (!state || !state.goods?.length)
          return state;
        
        state = {
          ...state,
          
          update: state.update + 1,
          goods: null,
          
          groupOrderTargets: [{ info: visitorInfo.info }]
        };
        
        calcQueryInfo(state);
        
        storeData(cart.shopCurrency, state);
        submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
        
        return state;
      }), 1);
    },
    
    clearFavorites: () => {
      setTimeout(() => setGoodsState((state) => {
        if (!state || !state.favorites?.length)
          return state;
        
        state = {
          ...state,
          
          update: state.update + 1,
          favorites: null
        };
        
        calcQueryInfo(state);
        
        storeFavoriteData(cart.shopCurrency, state);
        
        return state;
      }), 1);
    },
    
    // currency
    
    currency,
    
    currencyList: currencyList.data?.list || [DEFAULT_CURRENCY],
    
    findCurrency: (id) => currencyList.data?.list?.find((item) => item.id == id || item.code === id)
      || currency
      || currencyList.data?.list?.length && currencyList.data.list[0]
      || DEFAULT_CURRENCY,
    
    setCurrency: (id) => {
      const newCurrency = currencyList.data?.list?.find((item) => item.id == id);
      
      if (!newCurrency || newCurrency.id === currency.id)
        return;
      
      setCurrency(newCurrency);
    },
    
    // modal
    
    setModalRef: (value) => modalRef.current = value,
    
    setModalBtnRef: (value) => modalBtnRef.current = value,
    
    isModal: (el) => modalRef.current?.contains(el) || modalBtnRef.current?.contains(el),
    
    setModalVisible: (isModalVisible) => setGoodsState((state) => ({ ...state, isModalVisible })),
    
    toggleModalVisible: () => setGoodsState((state) => ({ ...state, isModalVisible: !state.isModalVisible })),
    
    // group order
    
    setGroupOrderModalVisible: (isGroupOrderModalVisible) => setGoodsState((state) => ({ ...state, isGroupOrderModalVisible })),
    
    get groupOrderCurrentTarget() {
      return groupOrderCurrent ?? cart.groupOrderTargets?.[0]?.info ?? visitorInfo.info ?? {};
    },
    
    setGroupOrderCurrentTarget: (target) => {
      setGroupOrderCurrent(target);
    },
    
    addGroupOrderTarget: (target) => {
      setGoodsState((state) => {
        if (!state || !visitorInfo.info)
          return state;
        
        state = {
          ...state,
          
          groupOrderTargets: !state.groupOrderTargets?.find((item) => item.info.id === target.id)
            ? [...(state.groupOrderTargets ?? (visitorInfo.info && [{ info: visitorInfo.info }]) ?? []), { info: target }]
            : state.groupOrderTargets
        };
        
        storeData(cart.shopCurrency, state);
        submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
        
        return state;
      });
      
      cart.setGroupOrderCurrentTarget(target);
    },
    
    removeGroupOrderTarget: (target) => {
      setGoodsState((state) => {
        if (!state || !visitorInfo.info)
          return state;
        
        state = {
          ...state,
          
          goods: state.goods?.filter((item) => item.target !== target.id),
          
          groupOrderTargets: state.groupOrderTargets?.filter((item) => item.info.id !== target.id),
        };
        
        storeData(cart.shopCurrency, state);
        submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
        
        return state;
      });
      
      if (target.id === cart.groupOrderCurrentTarget.id)
        cart.setGroupOrderCurrentTarget(visitorInfo.info);
    },
    
    setGroupOrderPIN(target, pin) {
      setGoodsState((state) => {
        if (!state || !visitorInfo.info)
          return state;
        
        state = {
          ...state,
          
          groupOrderTargets: state.groupOrderTargets?.map((item) => item.info.id === target.id ? { ...item, pin } : item)
        };
        
        storeData(cart.shopCurrency, state);
        submitData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, state);
        
        return state;
      });
    }
    
    //
  };
  
  cart.deliveryInfo = useCartDeliveryInfo(cart, langInfo?.code, visitorInfo);
  
  const warehouseCurrencyId = getWarehouseCurrencyId(cart.deliveryInfo.warehouse);
  const warehouseCurrencyCode = cart.deliveryInfo.warehouse?.currency || '';
  const warehouseCurrency = replaceCurrencyFromWarehouse
    ? currencyList.data?.list?.find((item) => item.id == warehouseCurrencyId || (warehouseCurrencyCode && item.code?.toLowerCase() === warehouseCurrencyCode))
    : null;
  cart.shopCurrency = warehouseCurrency || currency;
  
  React.useEffect(() => {
    if (!visitorId || !cart.shopCurrency.code)
      return;
    
    const loadIteration = ++loadDataIterationRef.current;
    
    const loadData = async () => {
      // storage data
      
      const startState = {
        update: 0,
        isLoading: true,
        
        message: null,
        isNext: true,
        isPromocode: false,
        syncCurrencyCode: cart.shopCurrency.code,
        
        goods: null,
        favorites: null,
        
        groupOrderTargets: null,
        warehouse: goodsState?.warehouse || null
      };
      
      storageLoadData(cart.shopCurrency, startState, 'goods');
      storageLoadData(cart.shopCurrency, startState, 'favorites');
      
      if (startState.groupOrderTargets?.length && Array.isArray(startState.groupOrderTargets[0]) && startState.groupOrderTargets[0][0] === visitorId) {
        const token = await api.getClientTokenGenerate(visitorId);
        
        const list = await Promise.all(startState.groupOrderTargets.map(async ([id, pin]) => ({ info: await api.getClientVisitor(id, { token }), pin })));
        
        startState.groupOrderTargets = startState.groupOrderTargets.map(([id, pin], index) => ({ info: list[index].info[id], pin }));
      } else {
        startState.groupOrderTargets = [{ info: visitorInfo.info }];
      }
      
      if (loadDataIterationRef.current !== loadIteration)
        return;
      
      calcQueryInfo(startState);
      setGoodsState(startState);
      
      if (loadDataIterationRef.current !== loadIteration)
        return;
      
      // database data
      
      fetchData(cart.shopCurrency, cart.deliveryInfo.warehouse?.id, startState);
    };
    
    loadData();
  }, [visitorId, cart.shopCurrency.code]);
  
  return (
    <CartContext.Provider value={cart}>
      {children}
    </CartContext.Provider>
  );
});

export const useCartContext = () => {
  const context = React.useContext(CartContext);
  
  if (!context)
    throw new Error('useCartContext should be used inside the CartProvider');
  
  return context;
};


const buildApiGoods = (list) => {
  if (!Array.isArray(list))
    return null;
  
  const result = [];
  
  const map = new Map();
  
  list.forEach((item) => {
    const id = convert.toInt(item[0]);
    const target = convert.toInt(item[6]);
    const count = convert.toUFloat(item[1]);
    
    if (!id || !count)
      return;
    
    const key = id + ':' + target;
    
    let goods = map.get(key);
    
    if (!goods) {
      goods = { id, target, group: [] };
      
      result.push(goods);
      
      map.set(key, goods);
    }
    
    const price = convert.toUFloat(item[2]);
    const discount = convert.toUFloat(item[3]);
    const points = convert.toUFloat(item[4]);
    const is_modifiable = !!item[5];
    
    goods.group.push({
      count,
      
      price,
      discount,
      points,
      
      is_modifiable
    });
  });
  
  return result;
};

const calcQueryInfo = (state) => {
  state.query = {};
  state.queryTarget = {};
  
  Object.entries(QUERY_INFO_FIELDS).forEach(([key, value]) => {
    let stateValue = 0;
    
    if (state.goods) {
      if (typeof value.byGoodsList === 'function')
        stateValue += Math.max(value.byGoodsList(state.goods), 0);
      
      if (typeof value.byGoodsGroup === 'function')
        state.goods.forEach((goods) => goods.group.forEach((group) => stateValue += Math.max(value.byGoodsGroup(group), 0)));
    }
    
    if (state.favorites) {
      if (typeof value.byFavoriteList === 'function')
        stateValue += Math.max(value.byFavoriteList(state.favorites), 0);
      
      if (typeof value.byFavoriteGroup === 'function')
        state.favorites.forEach((goods) => goods.group.forEach((group) => stateValue = Math.max(value.byFavoriteGroup(group), 0)));
    }
    
    state.query[key] = stateValue;
  });
  
  state.groupOrderTargets && state.groupOrderTargets.forEach((target) => {
    const query = state.queryTarget[target.info.id] = {};
    
    const goodsList = state.goods?.filter((goods) => goods.target === target.info.id);
    
    Object.entries(QUERY_INFO_FIELDS).forEach(([key, value]) => {
      let stateValue = 0;
      
      if (goodsList) {
        if (typeof value.byGoodsList === 'function')
          stateValue += Math.max(value.byGoodsList(goodsList), 0);
        
        if (typeof value.byGoodsGroup === 'function')
          goodsList.forEach((goods) => goods.group.forEach((group) => stateValue += Math.max(value.byGoodsGroup(group), 0)));
      }
      
      query[key] = stateValue;
    });
  });
};

const storageStoreData = (currency, state, type) => {
  const goods = state?.[type];
  
  const list = goods?.length ? goods.map((item) => [
      item.id,
      
      item.group.map((group) => [
        group.count,
        group.price,
        group.discount,
        group.points,
        group.is_modifiable
      ]),
      
      item.target
    ]) : null;
  
  if (type === 'goods') {
    const groupOrderTargets = state.groupOrderTargets?.map((target) => [target.info.id, target.pin || '']) ?? null;
    
    storage.set(STORAGE_CART_DATA_KEY, list && [currency.code, list, groupOrderTargets]);
  } else if (type === 'favorites') {
    storage.set(STORAGE_FAVORITE_DATA_KEY, list && [currency.code, list]);
  }
};

const storageLoadData = (currency, state, type) => {
  let data;
  
  if (type === 'goods') {
    data = storage.get(STORAGE_CART_DATA_KEY);
  } else if (type === 'favorites') {
    data = storage.get(STORAGE_FAVORITE_DATA_KEY);
  } else {
    return;
  }
  
  state[type] = [];
  
  if (!Array.isArray(data))
    return;
  
  const cartCurrency = data[0];
  const cartList = data[1];
  const cartGroupOrder = data[2];
  
  if (cartCurrency !== currency.code || !Array.isArray(cartList))
    return;
  
  cartList.forEach((cartItem) => {
    if (!Array.isArray(cartItem))
      return;
    
    const id = convert.toUInt(cartItem[0]);
    const group = cartItem[1];
    
    if (!id || !Array.isArray(group))
      return;
    
    const item = {
        id,
        group: group
          .map((groupItem) => Array.isArray(groupItem) && {
              count: convert.toUFloat(groupItem[0]),
              price: convert.toUFloat(groupItem[1]),
              discount: convert.toUFloat(groupItem[2]),
              points: convert.toUFloat(groupItem[3]),
              is_modifiable: !!groupItem[4],
              target: convert.toUInt(groupItem[5])
            })
          .filter((groupItem) => groupItem?.count > 0)
      };
    
    if (!item.group.length)
      return;
    
    if (type === 'goods' && Array.isArray(cartGroupOrder)) {
      state.groupOrderTargets = cartGroupOrder.map((target) => {
        if (!Array.isArray(target))
          return;
        
        const id = convert.toUInt(target[0]);
        
        if (!id)
          return;
        
        return [
          id,
          String(target[1] || '').trim() || ''
        ];
      }).filter(Boolean);
    }
    
    state[type].push(item);
  });
};

const serializeApiList = (state) => state?.goods?.map(({ id, target, group }) => [id, group.reduce((prev, item) => item.is_modifiable ? prev + item.count : prev, 0), target]) || [];
